import makeStyles from '@mui/styles/makeStyles';
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {user} from '../../../../store/reducers/user';
import {useTranslation} from 'react-i18next';
import Formatter from '../../../../util/formatter';
import moment from 'moment';
import Grid from '@mui/material/Grid';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import _ from 'lodash';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import config from '../../../../app/config';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.background + ` !important`,
    },
    groupRoot: {
        marginBottom: 10,
        padding: 5,
        borderRadius: 5,
    },
    paper: {
        padding: 10,
        backgroundColor: '#252525',
    },
    centerRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    userLog: {
        fontSize: 11,
        fontStyle: 'italic',
        color: theme.palette.mode === 'light' ? '#4B4B4B' : '#818181',
    },
    comment: {
        fontSize: 15,
        padding: 5,
        backgroundColor: theme.palette.background.default,
        borderRadius: 5,
    },
    clinician: {
        marginBottom: 10,
    },
    row: {
        width: '100%',
        marginBottom: 10,
    },
    divider: {
        margin: '10px 0',
    },
    label: {
        color: theme.palette.inPatient.questionText,
        fontSize: 12,
        fontWeight: 600,
        marginLeft: 10,
        marginBottom: 5,
    },
    topBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingBottom: 15,
        position: 'sticky',
        top: '0px',
        background: theme.palette.background.defaultBackground,
        zIndex: 11,
    },
    flexDivFullWidth: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },
    flexDiv: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    formName: {
        fontSize: 16,
        fontWeight: 600,
    },
    modalTitle: {
        textAlign: 'center',
        fontSize: 20,
        fontWeight: 'bold',
    },
}));

const BOARD_ROUND_FORM_INIT = {
    edd: null,
    cdd: null,
    optimisedToLeave: null,
    timeBoardRound: null,
    typeBoardRound: null,
    dischargeToday: null,
    greenDay: null,
    conclusion: '',
    who: '',
    date: '',
};

function BoardRound(props) {
    const classes = useStyles();
    const {t} = useTranslation();

    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);

    const [form, setForm] = useState(BOARD_ROUND_FORM_INIT);

    useEffect(() => {
        setForm((formLast) => {
            return {
                ...formLast,
                who: userFullName,
                date: moment().toISOString(),
            };
        });
    }, [userObject]);

    useEffect(() => {
        if (props.patient?.intendentManagement) {
            let intendentManagement = _.cloneDeep(props.patient?.intendentManagement);
            setForm((f) => ({
                ...f,
                edd: intendentManagement.edd,
                cdd: intendentManagement.cdd,
            }));
        }

        let nextBoardTimeConfig = config.boardRoundEvening;
        let nextBoardType = 'Evening';

        const hour = moment().hour();
        const hourEvening = moment(nextBoardTimeConfig, 'HH:mm').hour();

        if (hour < hourEvening - 2) {
            nextBoardType = 'Morning';
        }

        setForm((f) => ({
            ...f,
            timeBoardRound: nextBoardType,
        }));

    }, []);

    useEffect(() => {
        if (props.changeFormData) {
            props.changeFormData(form);
        }
    }, [form]);

    const handleFieldChange = (value, path) => {
        if (props.formIsChanged) props.formIsChanged(true);

        setForm((formLast) => {
            return {
                ...formLast,
                [path]: value,
            };
        });
    };

    return (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
                <div className={classes.label}>
                    {t('Estimated Discharge Date')}
                </div>
                <AgyleDatePicker
                    type="date"
                    value={form.edd}
                    disableFuture={false}
                    onChange={(tDate) => {
                        if (tDate) setForm({...form, edd: tDate.valueOf()});
                    }}
                    noBorder={false}
                    withNewBackground
                    size="large"
                />
            </Grid>

            <Grid item xs={12}>
                <div className={classes.label}>
                    {t('Confirmed Discharge Date')}
                </div>

                <AgyleDatePicker
                    type="date"
                    value={form.cdd}
                    disableFuture={false}
                    inputFormat={config.defaultDateTimeFormat}
                    onChange={(tDate) => {
                        if (tDate) setForm({...form, cdd: tDate.valueOf()});
                    }}
                    noBorder={false}
                    withNewBackground
                    size="large"
                />
            </Grid>


            <Grid item xs={10}>
                <div>{t('Time of Board Round')}</div>
            </Grid>

            <Grid item xs={2}>
                <AgyleBlockSwitch
                    onChange={(e) => handleFieldChange(e, 'timeBoardRound')}
                    value={form.timeBoardRound}
                    customLabels={{
                        yes: t('Morning'),
                        no: t('Evening'),
                    }}
                    valueObject={{
                        true: 'Morning',
                        false: 'Evening',
                    }}
                />
            </Grid>

            <Grid item xs={12} style={{borderBottom: '1px solid #626466'}}/>

            <Grid item xs={10}>
                <div>{t('Medically optimised to leave')}</div>
            </Grid>

            <Grid item xs={2}>
                <AgyleBlockSwitch
                    onChange={(e) => handleFieldChange(e, 'optimisedToLeave')}
                    value={form.optimisedToLeave}
                />
            </Grid>

            <Grid item xs={12} style={{borderBottom: '1px solid #626466'}}/>

            <Grid item xs={10}>
                <div>{t('Type of Board Round')}</div>
            </Grid>

            <Grid item xs={2}>
                <AgyleBlockSwitch
                    onChange={(e) => handleFieldChange(e, 'typeBoardRound')}
                    value={form.typeBoardRound}
                    customLabels={{
                        yes: t('Simple'),
                        no: t('Complex'),
                    }}
                    valueObject={{
                        true: 'Simple',
                        false: 'Complex',
                    }}
                />
            </Grid>
            <Grid item xs={12} style={{borderBottom: '1px solid #626466'}}/>

            <Grid item xs={10}>
                <div>{t('Discharge Today')}</div>
            </Grid>

            <Grid item xs={2}>
                <AgyleBlockSwitch
                    onChange={(e) => handleFieldChange(e, 'dischargeToday')}
                    value={form.dischargeToday}
                />
            </Grid>

            <Grid item xs={12} style={{borderBottom: '1px solid #626466'}}/>

            <Grid item xs={10}>
                <div>{t('Green Day')}</div>
            </Grid>

            <Grid item xs={2}>
                <AgyleBlockSwitch
                    onChange={(e) => handleFieldChange(e, 'greenDay')}
                    value={form.greenDay}
                />
            </Grid>

            <Grid item xs={12} style={{borderBottom: '1px solid #626466'}}/>

            <Grid item xs={12}>
                <AgyleTextField
                    label={t('Conclusion')}
                    onChange={event => setForm(prevState => ({
                        ...prevState,
                        conclusion: event.target.value,
                    }))}
                    value={form.conclusion}
                    debounce
                    multiline
                    minRows={5}
                    fullWidth
                    noBorder={false}
                    withNewBackground
                    size="large"
                />
            </Grid>
        </Grid>
    );
}

export default BoardRound;